import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Seat } from "./Seat";
import { getSeatLayout, type SeatStatus } from "@/lib/seat-utils";

interface SeatData {
  seatNumber: string;
  status: SeatStatus;
}

interface SeatGridProps {
  seats: SeatData[];
  totalSeats: number;
  selectedSeats: string[];
  ownHeldSeats: string[];
  onSeatClick: (seatNumber: string) => void;
  disabled?: boolean;
}

/**
 * Bus-shape seat grid with editorial chassis.
 * Driver cab at the top, rear at the bottom; aisle as thin stone track.
 */
export function SeatGrid({
  seats,
  totalSeats,
  selectedSeats,
  ownHeldSeats,
  onSeatClick,
  disabled,
}: SeatGridProps) {
  const { t } = useTranslation();

  const seatStatusMap = useMemo(() => {
    const map = new Map<string, SeatStatus>();
    seats.forEach((seat) => map.set(seat.seatNumber, seat.status));
    return map;
  }, [seats]);

  const layout = useMemo(() => getSeatLayout(totalSeats), [totalSeats]);

  const getEffectiveStatus = (seatNumber: string): SeatStatus => {
    if (selectedSeats.includes(seatNumber)) return "selected";
    return seatStatusMap.get(seatNumber) || "available";
  };

  return (
    <div
      className="overflow-x-auto pb-4"
      role="region"
      aria-label={t("a11y.seatGrid")}
    >
      <div className="mx-auto w-fit">
        {/* Driver cab — front of bus */}
        <div className="flex items-center gap-3 pb-3 border-b hairline-strong">
          <div className="flex items-center gap-2 text-eyebrow uppercase text-ink-muted">
            <span className="h-1.5 w-1.5 bg-brand" aria-hidden="true" />
            <span>{t("seats.busLayout.front", "Front")}</span>
          </div>
          <span className="flex-1 h-px bg-hairline" aria-hidden="true" />
          {/* Tiny steering wheel indicator */}
          <svg
            className="h-5 w-5 text-ink-subtle"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="9" />
            <circle cx="12" cy="12" r="2" fill="currentColor" />
            <path d="M12 3v6M5 12h4M15 12h4M12 15v6" />
          </svg>
        </div>

        {/* Cabin — cell-driven grid (5 columns: L1 L2 aisle R1 R2) */}
        <div
          className="grid items-center gap-1 sm:gap-1.5 py-5 px-4 sm:px-6 border-x hairline bg-stone-50/40"
          style={{ gridTemplateColumns: "auto auto minmax(1.25rem, auto) auto auto" }}
        >
          {layout.rows.flatMap((row) =>
            row.cells.map((cell) => {
              const gridRow = row.rowNumber;
              const gridColumn = cell.col + 1;

              if (cell.type === "seat") {
                return (
                  <div
                    key={`${row.rowNumber}-${cell.col}`}
                    style={{ gridColumn, gridRow }}
                    className="flex justify-center"
                  >
                    <Seat
                      seatNumber={cell.number}
                      status={getEffectiveStatus(cell.number)}
                      isOwnHold={ownHeldSeats.includes(cell.number)}
                      onClick={() => onSeatClick(cell.number)}
                      disabled={disabled}
                    />
                  </div>
                );
              }

              if (cell.type === "aisle") {
                return (
                  <span
                    key={`${row.rowNumber}-${cell.col}`}
                    style={{ gridColumn, gridRow }}
                    className="w-4 sm:w-6 h-px bg-hairline-strong justify-self-center"
                    aria-hidden="true"
                  />
                );
              }

              if (cell.type === "toilet") {
                return (
                  <div
                    key={`${row.rowNumber}-${cell.col}`}
                    style={{
                      gridColumn: `${gridColumn} / span ${cell.colSpan}`,
                      gridRow: `${gridRow} / span ${cell.rowSpan}`,
                    }}
                    className="m-0.5 flex flex-col items-center justify-center gap-1 border border-hairline-strong bg-stone-100 text-ink-muted"
                    role="img"
                    aria-label={t("seats.busLayout.toilet", "Restroom")}
                  >
                    <svg
                      className="h-5 w-5"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M6 4v7a3 3 0 0 0 3 3h0a3 3 0 0 0 3-3" />
                      <path d="M6 7h6" />
                      <path d="M9 14v6" />
                      <path d="M6 20h6" />
                      <circle cx="18" cy="6" r="2" />
                      <path d="M16.5 20v-4a1.5 1.5 0 0 1 3 0v4" />
                    </svg>
                    <span className="text-[0.6rem] font-medium uppercase tracking-wide">
                      {t("seats.busLayout.toilet", "WC")}
                    </span>
                  </div>
                );
              }

              // empty / toilet-cont: occupy no visible cell
              return null;
            }),
          )}
        </div>

        {/* Rear */}
        <div className="flex items-center gap-3 pt-3 border-t hairline-strong">
          <span className="flex-1 h-px bg-hairline" aria-hidden="true" />
          <span className="text-eyebrow uppercase text-ink-subtle">
            {t("seats.busLayout.back", "Rear")}
          </span>
          <span className="flex-1 h-px bg-hairline" aria-hidden="true" />
        </div>
      </div>
    </div>
  );
}
